import {withWriteIntents} from './write-intents';
import { empty, intval, strcasecmp, strval, strtoupper, strtolower, floatval, in_array, phpCopy } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
import { slot, array, isArray, values, scopeValue, requireStringArgument, type Arr } from './access';
import { catalogVariableName } from './catalog';
export interface ArcaneArrowDeps {
    layer_entries(row: Arr, name: string): Arr;
    check_uuid(value: unknown): boolean;
    passive_ability_entry_is_active(entry: Arr): boolean;
    layer_value(row: Arr, name: string): unknown;
    _ft_ability_entry_layer_src(row: Arr): Arr;
    ability_entry_id(entry: unknown): unknown;
    inventory_field(row: Arr, name: string): unknown;
    item_field(row: Arr, name: string): unknown;
    janus_trigger_in_progress(): boolean;
    ft_encounter_context(row: Arr): Arr;
    encounter_field(row: Arr, name: string): unknown;
    pos_coord(row: Arr, axis: string): unknown;
}
export interface ArcaneArrowResolveDeps extends ArcaneArrowDeps {
    search_encounter_arr_for_item_arr(encounter: Arr, campaign: Arr | null, id: string): unknown;
    delete_inventory(caster: PhpRef<Arr>, affected: PhpRef<Arr>, type: string, qualifier: string,
        id: string, roll: string, qty: number, show: boolean): void;
    query_d_item_arr(id: string): Arr;
    janus_trigger_enter(spell: Arr): void;
    janus_trigger_exit(): void;
    janus(id: string, caster: PhpRef<Arr>, targets: Arr, affected: PhpRef<Arr>, privacy: number,
        rawMod: number, rawAdv: number, roll: string, encounter: PhpRef<Arr | null>, campaign: Arr | null,
        location: Arr | null, var1: null, arr1: null, var2: string, arr2: Arr, weapon: null,
        ability: Arr, connection: string, castingTargets: string): void;
}
/** PHP: ft_arcane_arrow_resolve (includes/functions/janus/core/arcane_fletcher.php:329) */
export function ftArcaneArrowResolve(input: Arr, context: Arr, env: PortEnv<ArcaneArrowResolveDeps>) {
    return withWriteIntents(env, env => {
        const refs = phpCopy(input);
        const contextValue = (key: string): unknown => {
            if (!array(context).has(key)) env.warn('Undefined array key "' + key + '"', 2);
            return slot(context, key);
        };
        let struckId = '';
        const hitIds = slot(refs, 'targets_hit_arr') ?? [];
        for (const id of isArray(hitIds) ? values(hitIds) : [hitIds])
            if (typeof id === 'string' && env.deps.check_uuid(id)) { struckId = id; break; }
        const encounter = { value: slot(refs, 'encounter_arr') as Arr | null }, campaign = slot(refs, 'campaign_arr') as Arr | null;
        const struck = struckId !== '' && isArray(encounter.value)
            ? env.deps.search_encounter_arr_for_item_arr(encounter.value, campaign, struckId) : null;
        const spell = contextValue('spell_arr') as Arr, caster = { value: slot(refs, 'caster_arr') as Arr };
        const affected = { value: slot(refs, 'affected_ids') as Arr }, rollValue = scopeValue(refs, 'roll_id', env);
        const scrollId = strval(contextValue('scroll_inventory_id'));
        const roll = requireStringArgument(rollValue, 'delete_inventory', 6, 'roll_id');
        env.deps.delete_inventory(caster, affected, 'inventory_deleted', 'consumed', scrollId, roll, 1, true);
        if (isArray(struck) && !empty(struck)) {
            const aoe = env.deps.layer_value(spell, 'aoe_type') !== null;
            let casting = struckId, targets: Arr[] = [struck];
            if (aoe) {
                casting = ftArcaneArrowAoeCastingString(caster.value, struck, spell, encounter.value, env);
                const point = casting !== '' ? env.deps.query_d_item_arr(casting) : [];
                targets = !empty(point) ? [point] : [];
            }
            if (targets.length) {
                const previous = env.globals.ft_arcane_arrow_delivery ?? null;
                env.globals.ft_arcane_arrow_delivery = { spell_id: strval(slot(context, 'spell_id')),
                    scroll_inventory_id: strval(slot(context, 'scroll_inventory_id')),
                    caster_id: strval(slot(caster.value, 'encounter_creatures', 'encounter_creature_id') ?? ''), struck_id: struckId,
                    snapshot: isArray(slot(refs, 'attack_skill_roll_arr')) ? phpCopy(slot(refs, 'attack_skill_roll_arr')) : null };
                env.deps.janus_trigger_enter(spell);
                try {
                    env.deps.janus(strval(slot(context, 'spell_id')), caster, targets, affected, Number(intval(slot(refs, 'roll_private') ?? 0)),
                        0, 0, roll, encounter, campaign, slot(refs, 'location_arr') as Arr | null, null, null,
                        'arcane_arrow_cast', slot(context, 'scroll_arr') as Arr, null, [], env.uuid(), casting);
                } finally { env.deps.janus_trigger_exit(); env.globals.ft_arcane_arrow_delivery = previous; }
            }
        }
        return null;
    });
}
/** PHP: ft_arcane_fletcher_provider (includes/functions/janus/core/arcane_fletcher.php:37) */
export function ftArcaneFletcherProvider(caster: Arr, env: PortEnv<ArcaneArrowDeps>): string | null {
    for (let entry of values(env.deps.layer_entries(caster, 'passive_ability'))) {
        if (typeof entry === 'string' && env.deps.check_uuid(entry)) entry = { item_id: entry };
        if (!isArray(entry) || !env.deps.passive_ability_entry_is_active(entry)) continue;
        if (empty(env.deps.layer_value(env.deps._ft_ability_entry_layer_src(entry), 'arcane_fletcher'))) continue;
        const id = strval(env.deps.ability_entry_id(entry));
        if (id !== '' && env.deps.check_uuid(id)) return id;
    }
    return null;
}
/** PHP: ft_arcane_arrow_find_carried_row (includes/functions/janus/core/arcane_fletcher.php:60) */
export function ftArcaneArrowFindCarriedRow(caster: Arr, id: string, env: PortEnv<ArcaneArrowDeps>): Arr | null {
    return closure62FindCarriedRow(slot(caster, 'inventory') ?? null, id, env);
}
/** PHP: {closure@62:153} (includes/functions/janus/core/arcane_fletcher.php:62) */
export function closure62FindCarriedRow(entries: unknown, id: string, env: PortEnv<ArcaneArrowDeps>): Arr | null {
    if (!isArray(entries)) return null;
    for (const entry of values(entries)) {
        if (!isArray(entry)) continue;
        if (intval(env.deps.inventory_field(entry, 'inventory_deleted') ?? 0) === 0 && strcasecmp(strval(env.deps.inventory_field(entry, 'inventory_id') ?? ''), id) === 0) return phpCopy(entry);
        const nested = closure62FindCarriedRow(slot(entry, 'inventory_in_container') ?? null, id, env);
        if (nested !== null) return nested;
    }
    return null;
}
/** PHP: ft_arcane_arrow_scroll_spell_id (includes/functions/janus/core/arcane_fletcher.php:89) */
export function ftArcaneArrowScrollSpellId(...args: [...Arr[], PortEnv<ArcaneArrowDeps>]): string | null {
    const env = args[args.length - 1] as PortEnv<ArcaneArrowDeps>, rows = args.slice(0, -1) as Arr[];
    for (const row of rows) {
        for (const entry of values((slot(row, 'computed', 'enchantment', 'abilities', 'charged_spell') ?? []) as Arr)) {
            const id = strval(env.deps.ability_entry_id(entry));
            if (id !== '' && env.deps.check_uuid(id)) return id;
        }
        for (const enchantment of values((slot(row, 'enchantment_template_enchantments') ?? []) as Arr)) {
            if (isArray(enchantment) && (slot(enchantment, 'enchantment_type') ?? null) === 'charged_spell'
                && env.deps.check_uuid(strval(slot(enchantment, 'enchantment_item_id') ?? ''))) return strval(slot(enchantment, 'enchantment_item_id'));
        }
    }
    return null;
}
/** PHP: ft_arcane_arrow_delivery (includes/functions/janus/core/arcane_fletcher.php:222) */
export function ftArcaneArrowDelivery(env: PortEnv<Pick<ArcaneArrowDeps, 'janus_trigger_in_progress'>>): Arr | null {
    const delivery = env.globals.ft_arcane_arrow_delivery ?? null;
    return isArray(delivery) && env.deps.janus_trigger_in_progress() ? phpCopy(delivery) : null;
}
/** PHP: ft_arcane_arrow_delivery_matches (includes/functions/janus/core/arcane_fletcher.php:232) */
export function ftArcaneArrowDeliveryMatches(ability: Arr | null, marker: unknown, env: PortEnv<Pick<ArcaneArrowDeps, 'janus_trigger_in_progress' | 'item_field'>>): boolean {
    if (marker !== 'arcane_arrow_cast' || !isArray(ability)) return false;
    const delivery = ftArcaneArrowDelivery(env);
    if (delivery === null) return false;
    const id = strval(env.deps.item_field(ability, 'item_id') ?? slot(ability, 'item_id_used', 0) ?? '');
    return strcasecmp(id, strval(slot(delivery, 'spell_id'))) === 0;
}
/** PHP: ft_arcane_arrow_range_waived (includes/functions/janus/core/arcane_fletcher.php:249) */
export function ftArcaneArrowRangeWaived(ability: Arr, casterId: string | null, target: unknown, env: PortEnv<ArcaneArrowDeps>): boolean {
    const delivery = ftArcaneArrowDelivery(env);
    if (delivery === null || !isArray(target) || !empty(slot(delivery, 'range_waived'))) return false;
    const id = strval(env.deps.item_field(ability, 'item_id') ?? slot(ability, 'item_id_used', 0) ?? '');
    const waived = strcasecmp(id, strval(slot(delivery, 'spell_id'))) === 0 && strcasecmp(strval(casterId), strval(slot(delivery, 'caster_id') ?? '')) === 0
        && strcasecmp(strval(slot(target, 'encounter_creatures', 'encounter_creature_id') ?? ''), strval(slot(delivery, 'struck_id'))) === 0;
    if (waived) { const updated = array(env.globals.ft_arcane_arrow_delivery as Arr); updated.set('range_waived', true); env.globals.ft_arcane_arrow_delivery = updated; }
    return waived;
}
/** PHP: ft_arcane_arrow_reused_attack_snapshot (includes/functions/janus/core/arcane_fletcher.php:270) */
export function ftArcaneArrowReusedAttackSnapshot(ability: Arr, marker: unknown, targetId: string, env: PortEnv<ArcaneArrowDeps>): Arr | null {
    if (!ftArcaneArrowDeliveryMatches(ability, marker, env)) return null;
    const delivery = ftArcaneArrowDelivery(env);
    if (strcasecmp(targetId, strval(slot(delivery, 'struck_id') ?? '')) !== 0) return null;
    const input = slot(delivery, 'snapshot') ?? null;
    if (!isArray(input)) return null;
    const snapshot = array(input), rt = env.deps.layer_value(ability, 'roll_type');
    if (typeof rt === 'string' && rt !== '') {
        const type = array((slot(snapshot, 'roll_type_1') ?? []) as Arr); type.set(0, rt); snapshot.set('roll_type_1', type);
    }
    return snapshot;
}
/** PHP: ft_arcane_arrow_aoe_casting_string (includes/functions/janus/core/arcane_fletcher.php:296) */
export function ftArcaneArrowAoeCastingString(caster: Arr, struck: Arr, spell: Arr, encounter: Arr | null, env: PortEnv<ArcaneArrowDeps>): string {
    const id = strval(env.deps.encounter_field(encounter ?? env.deps.ft_encounter_context(caster), 'encounter_id') ?? '');
    if (!env.deps.check_uuid(id)) return '';
    const ox = Number(floatval(env.deps.pos_coord(struck, 'x_pos') ?? 0)), oy = Number(floatval(env.deps.pos_coord(struck, 'y_pos') ?? 0)), oz = Number(floatval(env.deps.pos_coord(struck, 'z_pos') ?? 0));
    let dx = ox, dy = oy;
    const uuid = strval(env.deps.layer_value(spell, 'aoe_type') ?? '');
    const name = env.deps.check_uuid(uuid) ? strtolower(strval(catalogVariableName('AoeType', uuid, env) ?? '')) : strtolower(uuid);
    if (!in_array(name, ['sphere', 'circle', 'cube', 'square', 'cylinder'], true)) {
        const distance = Number(floatval(env.deps.layer_value(spell, 'aoe_distance') ?? 0));
        const vx = ox - Number(floatval(env.deps.pos_coord(caster, 'x_pos') ?? ox)), vy = oy - Number(floatval(env.deps.pos_coord(caster, 'y_pos') ?? oy));
        const length = Math.sqrt(vx * vx + vy * vy);
        if (length < 0.01) dx = ox + distance;
        else { dx = ox + vx / length * distance; dy = oy + vy / length * distance; }
    }
    return id + '|' + [ox, oy, oz, dx, dy, oz].map(value => strval(floatval(value))).join(':');
}
